import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { ActionIcon, Avatar, Button, Checkbox, Drawer, FileButton, Textarea } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import {
  IconCircleCheck,
  IconCornerUpLeft,
  IconDownload,
  IconExternalLink,
  IconFile,
  IconFileText,
  IconMessageCircle,
  IconPaperclip,
  IconPlus,
  IconSend2,
  IconX,
} from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { createReference, type MedplumClient } from '@medplum/core'
import { downloadAttachmentFile } from '@/utils/downloadAttachment'
import { useUnlinkOutgoingFax } from '@/hooks/useFaxTasks'
import {
  checklistCounts,
  useBoardMutations,
  type BoardAttachment,
  type BoardItem,
  type BoardNote,
  type CellRef,
  type ChecklistStep,
} from './hooks'
import { formatNoteDate, formatNoteTimestamp } from './time'
import { useResizableDrawer } from '@/hooks/useResizableDrawer'
import { AiBadge } from './AiBadge'
import { IdentityTaskPanel } from './IdentityTaskPanel'
import { buildPatientChartPath } from '@/pages/Patient/patientChartPath'
import { isAiGenerated } from '@/pages/CareCoordination/queueFilters'

const MAX_ATTACHMENTS = 5
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

type SubmitNote = (
  text: string,
  parentNoteId?: string,
  attachments?: BoardAttachment[],
) => Promise<boolean>

// Upload each file to a Binary with the caller's own Medplum client (regular
// users can create Binary), tying it to their profile via securityContext. Store
// a RELATIVE `Binary/{id}` url so it resolves against the viewer's public base.
async function uploadAttachments(
  medplum: MedplumClient,
  files: File[],
): Promise<BoardAttachment[]> {
  const profile = medplum.getProfile()
  return Promise.all(
    files.map(async (file) => {
      const contentType = file.type || 'application/octet-stream'
      const binary = await medplum.createBinary({
        data: file,
        contentType,
        ...(profile ? { securityContext: createReference(profile) } : {}),
      })
      return { url: `Binary/${binary.id}`, title: file.name, contentType, size: file.size }
    }),
  )
}

// Text + file composer shared by the top-level update box and the per-note reply
// box, so both can attach files with identical validation/upload behaviour.
const NoteComposer = ({
  placeholder,
  submitLabel,
  pending,
  autoFocus,
  onSubmit,
  onCancel,
}: {
  placeholder: string
  submitLabel: string
  pending: boolean
  autoFocus?: boolean
  onSubmit: (text: string, attachments: BoardAttachment[]) => Promise<boolean>
  onCancel?: () => void
}) => {
  const medplum = useMedplum()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const resetFileRef = useRef<() => void>(null)
  const busy = pending || uploading

  const addFiles = (picked: File[]) => {
    const sized = picked.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        showNotification({ color: 'red', message: `“${file.name}” is larger than 10 MB` })
        return false
      }
      return true
    })
    setFiles((prev) => {
      const combined = [...prev, ...sized]
      if (combined.length > MAX_ATTACHMENTS) {
        showNotification({ color: 'red', message: `Up to ${MAX_ATTACHMENTS} files` })
        return combined.slice(0, MAX_ATTACHMENTS)
      }
      return combined
    })
    resetFileRef.current?.()
  }

  const submit = async () => {
    if (!text.trim() || busy) return
    let attachments: BoardAttachment[] = []
    if (files.length > 0) {
      setUploading(true)
      try {
        attachments = await uploadAttachments(medplum, files)
      } catch {
        showNotification({
          color: 'red',
          title: 'Upload failed',
          message: 'Could not upload files',
        })
        setUploading(false)
        return
      }
      setUploading(false)
    }
    if (await onSubmit(text, attachments)) {
      setText('')
      setFiles([])
    }
  }

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white px-3.5 py-3 transition-colors focus-within:border-[#275587]">
      <Textarea
        variant="unstyled"
        autoFocus={autoFocus}
        autosize
        minRows={2}
        placeholder={placeholder}
        value={text}
        disabled={busy}
        onChange={(e) => setText(e.currentTarget.value)}
        styles={{ input: { padding: 0 } }}
      />
      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {files.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className="flex items-center gap-1 rounded-full bg-gray-100 py-0.5 pr-1 pl-2 text-xs"
            >
              <span className="max-w-[160px] truncate">{file.name}</span>
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                aria-label={`Remove ${file.name}`}
                onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
              >
                <IconX size={12} />
              </ActionIcon>
            </span>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <FileButton
          multiple
          accept="image/*,application/pdf"
          onChange={addFiles}
          resetRef={resetFileRef}
        >
          {(props) => (
            <Button
              {...props}
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<IconPaperclip size={14} />}
              disabled={files.length >= MAX_ATTACHMENTS}
            >
              Attach
            </Button>
          )}
        </FileButton>
        <div className="flex gap-2">
          {onCancel && (
            <Button size="xs" variant="subtle" color="gray" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button size="xs" loading={busy} disabled={!text.trim()} onClick={() => void submit()}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

const AttachmentList = ({ attachments }: { attachments: BoardAttachment[] }) => {
  const medplum = useMedplum()
  return (
    <div className="mt-2 flex flex-col gap-1">
      {attachments.map((attachment, index) => (
        <button
          key={`${attachment.url}-${index}`}
          type="button"
          onClick={() =>
            void downloadAttachmentFile(medplum, attachment.url, attachment.title).catch(() =>
              showNotification({ color: 'red', message: 'Could not download attachment' }),
            )
          }
          className="hover:text-primary flex w-fit max-w-full cursor-pointer items-center gap-1.5 rounded border border-[#EEEEEE] px-2 py-1 text-left text-xs text-gray-600 transition"
        >
          <IconFile size={13} className="shrink-0 text-gray-400" />
          <span className="truncate">{attachment.title}</span>
          <IconDownload size={12} className="shrink-0 text-gray-400" />
        </button>
      ))}
    </div>
  )
}

// One editable checklist row: toggle done, edit the title in place, or delete it.
// The title is a controlled input resynced to the server value (on step.title
// change) so a refetch after a save doesn't strand a stale edit.
const ChecklistRow = ({
  step,
  onToggle,
  onRename,
  onDelete,
}: {
  step: ChecklistStep
  onToggle: (done: boolean) => void
  onRename: (title: string) => void
  onDelete: () => void
}) => {
  const [value, setValue] = useState(step.title)
  useEffect(() => setValue(step.title), [step.title])

  const commit = () => {
    const trimmed = value.trim()
    // Empty isn't a valid step title — revert to the last saved value.
    if (!trimmed) {
      setValue(step.title)
      return
    }
    if (trimmed !== step.title) onRename(trimmed)
  }

  return (
    <div className="group flex items-start gap-2">
      <Checkbox
        className="mt-0.5"
        checked={step.done}
        onChange={(e) => onToggle(e.currentTarget.checked)}
        aria-label={step.title}
      />
      {/* A textarea (not an input) so long titles wrap instead of clipping; it
          autosizes to fit. Enter commits (Shift+Enter would add a newline). */}
      <Textarea
        variant="unstyled"
        size="sm"
        autosize
        minRows={1}
        className="flex-1"
        styles={{ input: { padding: 0 } }}
        classNames={{ input: step.done ? 'text-gray-400 line-through' : undefined }}
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
      />
      <ActionIcon
        variant="subtle"
        color="gray"
        size="sm"
        aria-label={`Delete step: ${step.title}`}
        className="opacity-0 transition group-hover:opacity-100"
        // Keep focus off the input so its onBlur commit can't race the delete.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onDelete}
      >
        <IconX size={14} />
      </ActionIcon>
    </div>
  )
}

// Editable checklist, shown on every task. Toggling a step is optimistic (see
// useBoardMutations); structural edits — add, rename, delete — replace the whole
// list through onSave (boardItemUpdate). AI-generated steps (from a progress
// note's Plan) and manually added ones are indistinguishable once created.
const ChecklistSection = ({
  item,
  onToggle,
  onSave,
}: {
  item: BoardItem
  onToggle: (stepId: string, done: boolean) => void
  onSave: (checklist: ChecklistStep[]) => void
}) => {
  const checklist = item.checklist ?? []
  const { done, total } = checklistCounts(item)
  const [newTitle, setNewTitle] = useState('')

  const addStep = () => {
    const trimmed = newTitle.trim()
    if (!trimmed) return
    onSave([...checklist, { id: crypto.randomUUID(), title: trimmed, done: false }])
    setNewTitle('')
  }

  return (
    <div className="rounded-md border border-[#EEEEEE] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium">Checklist</span>
        {total > 0 && (
          <span className="text-xs text-gray-400">
            {done} of {total} completed
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {checklist.map((step) => (
          <ChecklistRow
            key={step.id}
            step={step}
            onToggle={(d) => onToggle(step.id, d)}
            onRename={(title) =>
              onSave(checklist.map((s) => (s.id === step.id ? { ...s, title } : s)))
            }
            onDelete={() => onSave(checklist.filter((s) => s.id !== step.id))}
          />
        ))}
        <div className="flex items-start gap-2 pl-0.5">
          {/* The leading "+" is the add control: click it (or press Enter in the
              field) to append the item. */}
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            className="mt-0.5"
            aria-label="Add checklist item"
            // Keep focus in the field so several items can be added in a row.
            onMouseDown={(e) => e.preventDefault()}
            onClick={addStep}
          >
            <IconPlus size={16} />
          </ActionIcon>
          <Textarea
            variant="unstyled"
            size="sm"
            autosize
            minRows={1}
            className="flex-1"
            styles={{ input: { padding: 0 } }}
            placeholder="Add an item"
            value={newTitle}
            onChange={(e) => setNewTitle(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                addStep()
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}

const NoteAuthor = ({
  display,
  createdAt,
  size = 'sm',
}: {
  display: string
  createdAt: string
  size?: 'sm' | 'xs'
}) => (
  <div className="flex items-center gap-2">
    <Avatar name={display} color="#275587" size={size} radius="xl" />
    <span className="text-sm font-medium text-gray-800">{display}</span>
    <span className="text-xs text-gray-300">·</span>
    <span className="text-xs text-gray-400" title={new Date(createdAt).toLocaleString()}>
      {formatNoteTimestamp(createdAt)}
    </span>
  </div>
)

const NoteCard = ({
  note,
  onReply,
  pending,
}: {
  note: BoardNote
  onReply: SubmitNote
  pending: boolean
}) => {
  const [replying, setReplying] = useState(false)

  return (
    <div className="rounded-xl border border-[#EEEEEE] bg-white p-4 transition-colors hover:border-[#D8DCE6]">
      <NoteAuthor display={note.author.display} createdAt={note.createdAt} />
      <p className="mt-2.5 text-sm leading-relaxed whitespace-pre-wrap text-gray-700">
        {note.text}
      </p>
      {note.attachments && note.attachments.length > 0 && (
        <AttachmentList attachments={note.attachments} />
      )}

      {note.replies.length > 0 && (
        <div className="mt-4 flex flex-col gap-4 border-l-2 border-[#EFF1F5] pl-4">
          {note.replies.map((replyEntry) => (
            <div key={replyEntry.id}>
              <NoteAuthor
                display={replyEntry.author.display}
                createdAt={replyEntry.createdAt}
                size="xs"
              />
              <p className="mt-1.5 text-sm leading-relaxed whitespace-pre-wrap text-gray-700">
                {replyEntry.text}
              </p>
              {replyEntry.attachments && replyEntry.attachments.length > 0 && (
                <AttachmentList attachments={replyEntry.attachments} />
              )}
            </div>
          ))}
        </div>
      )}

      {replying ? (
        <div className="mt-3">
          <NoteComposer
            placeholder="Write a reply…"
            submitLabel="Reply"
            pending={pending}
            autoFocus
            onSubmit={async (text, attachments) => {
              const ok = await onReply(text, note.id, attachments)
              if (ok) setReplying(false)
              return ok
            }}
            onCancel={() => setReplying(false)}
          />
        </div>
      ) : (
        <button
          type="button"
          className="hover:text-primary mt-3 flex cursor-pointer items-center gap-1 text-xs font-medium text-gray-400 transition"
          onClick={() => setReplying(true)}
        >
          <IconCornerUpLeft size={13} />
          Reply
        </button>
      )}
    </div>
  )
}

export const ItemDrawer = ({ item, onClose }: { item: BoardItem | null; onClose: () => void }) => {
  const { addNote, toggleChecklistStep, updateItem } = useBoardMutations(item?.boardId)
  const unlinkFax = useUnlinkOutgoingFax()

  // Drag the panel's left edge to resize, remembered across sessions.
  const { width, resizing, startResize } = useResizableDrawer('careQueueDrawerWidth')

  // Structural checklist edits (add/rename/delete) replace the whole list. The
  // hook reports its own errors and invalidates the board, which reflows a fresh
  // checklist back into this drawer via CareQueueTab's live `selectedItem`.
  const saveChecklist = (checklist: ChecklistStep[]) => {
    if (!item) return
    void updateItem.mutateAsync({ itemId: item.id, checklist }).catch(() => undefined)
  }

  // Total updates on the task: top-level notes plus their replies.
  const updateCount = item?.notes.reduce((sum, note) => sum + 1 + note.replies.length, 0) ?? 0

  const submitNote: SubmitNote = async (text, parentNoteId, attachments) => {
    const trimmed = text.trim()
    if (!trimmed || !item || addNote.isPending) return false
    try {
      await addNote.mutateAsync({
        itemId: item.id,
        text: trimmed,
        ...(parentNoteId ? { parentNoteId } : {}),
        ...(attachments?.length ? { attachments } : {}),
      })
      return true
    } catch {
      // Error notification handled by the mutation hook.
      return false
    }
  }

  return (
    <>
      {/* Full-height drag target straddling the panel's left edge, showing a
          short centered grip pill (like a resizable sidebar) on hover/while
          dragging. Portaled + fixed so it sits centered on the seam without the
          panel's overflow clipping its outer half. */}
      {item &&
        createPortal(
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            onPointerDown={startResize}
            style={{ right: width }}
            className="group fixed inset-y-0 z-[300] flex w-3 translate-x-1/2 cursor-ew-resize items-center justify-center"
          >
            <span
              className={`h-9 w-1 rounded-full transition-colors ${
                resizing ? 'bg-gray-400' : 'bg-transparent group-hover:bg-gray-300'
              }`}
            />
          </div>,
          document.body,
        )}
      <Drawer.Root opened={!!item} onClose={onClose} position="right" size={width}>
        <Drawer.Overlay backgroundOpacity={0.2} style={{ top: 64 }} />
        <Drawer.Content>
          <Drawer.Header>
            <Drawer.Title>
              <span className="flex items-center gap-2">
                <span className="text-lg font-medium">{item?.name}</span>
                {item && isAiGenerated(item) && <AiBadge />}
              </span>
            </Drawer.Title>
            <Drawer.CloseButton />
          </Drawer.Header>
          <Drawer.Body>
            {item && (
              <div className="flex flex-col gap-4">
                {item.identityPatientId && (
                  <IdentityTaskPanel patientId={item.identityPatientId} boardId={item.boardId} />
                )}
                {(item.sourcePractitioner ||
                  item.sourceNoteDate ||
                  item.sourceDocumentReferenceId) &&
                  // Lab-sourced AND fax-sourced tasks also set sourcePractitioner
                  // (the triaging/acting practitioner, used as the notification
                  // sender), so this branch must explicitly defer to each of the
                  // branches below rather than relying on the source shapes never
                  // overlapping. Any new source shape must be added here too.
                  !item.sourceDiagnosticReportId &&
                  !item.sourceFaxDocumentReferenceId &&
                  !item.sourceLetterRequestId &&
                  (() => {
                    // The patient cell holds a `Patient/…` ref; person cells hold
                    // `Practitioner/…`, so scanning the values for a Patient ref finds
                    // the patient without needing the board's column metadata here.
                    const patientCell = Object.values(item.values).find(
                      (v): v is CellRef =>
                        typeof v === 'object' && v !== null && v.reference.startsWith('Patient/'),
                    )
                    const patientId = patientCell?.reference.split('/')[1]
                    // Deep-link to the exact note when its DocumentReference id was
                    // stored; otherwise the patient's progress-notes section (newest
                    // note) as a fallback for tasks created before that was captured.
                    const href = patientId
                      ? buildPatientChartPath(
                          patientId,
                          'progress-notes',
                          item.sourceDocumentReferenceId,
                        )
                      : undefined
                    const label = (
                      <>
                        {item.sourcePractitioner
                          ? `From ${item.sourcePractitioner.display}'s progress note`
                          : 'From a progress note'}
                        {item.sourceNoteDate ? ` · ${formatNoteDate(item.sourceNoteDate)}` : ''}
                      </>
                    )
                    return (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconFileText size={14} className="shrink-0" />
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary inline-flex items-center gap-1 underline-offset-2 transition hover:underline"
                          >
                            <span>{label}</span>
                            <IconExternalLink size={12} className="shrink-0" />
                          </a>
                        ) : (
                          <span>{label}</span>
                        )}
                      </div>
                    )
                  })()}

                {item.sourceDiagnosticReportId &&
                  (() => {
                    const patientCell = Object.values(item.values).find(
                      (v): v is CellRef =>
                        typeof v === 'object' && v !== null && v.reference.startsWith('Patient/'),
                    )
                    const patientId = patientCell?.reference.split('/')[1]
                    const href = patientId
                      ? buildPatientChartPath(
                          patientId,
                          'labs-imaging',
                          item.sourceDiagnosticReportId,
                        )
                      : undefined
                    const label = (
                      <>
                        {item.sourceLabName
                          ? `From a ${item.sourceLabName} lab result`
                          : 'From a lab result'}
                        {item.sourceReceivedAt ? ` · ${formatNoteDate(item.sourceReceivedAt)}` : ''}
                      </>
                    )
                    return (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconFileText size={14} className="shrink-0" />
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary inline-flex items-center gap-1 underline-offset-2 transition hover:underline"
                          >
                            <span>{label}</span>
                            <IconExternalLink size={12} className="shrink-0" />
                          </a>
                        ) : (
                          <span>{label}</span>
                        )}
                      </div>
                    )
                  })()}

                {/* Fax provenance (PD-1513). Linked back to the fax inbox only
                    when sourceFaxTaskId (the fax-triage Task id) is present —
                    the fax route is /fax/inbox/<Task id>, a different resource
                    from sourceFaxDocumentReferenceId (the document, used as the
                    lookup/idempotency key). Items created before this field
                    existed have no Task id and fall back to plain text: no link
                    beats a wrong one. */}
                {item.sourceFaxDocumentReferenceId && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <IconFileText size={14} className="shrink-0" />
                    {item.sourceFaxTaskId ? (
                      <Link
                        to={`/fax/inbox/${item.sourceFaxTaskId}`}
                        className="hover:text-primary inline-flex items-center gap-1 underline-offset-2 transition hover:underline"
                      >
                        <span>From an incoming fax</span>
                        <IconExternalLink size={12} className="shrink-0" />
                      </Link>
                    ) : (
                      <span>From an incoming fax</span>
                    )}
                  </div>
                )}

                {/* Sent faxes recorded against this task (PD-1606). Deliberately
                    NOT gated on sourceFaxDocumentReferenceId: any task can have a
                    fax recorded against it, not only one created from a fax.
                    A fax deleted from RingCentral leaves its row here — recipient
                    and date were snapshotted at link time, so the record still
                    reads correctly even once the link stops resolving. */}
                {!!item.outgoingFaxes?.length && (
                  <div className="rounded-md border border-[#EEEEEE] p-3">
                    <p className="text-xs font-medium text-gray-500">Sent faxes</p>
                    {item.outgoingFaxes.map((fax) => (
                      <div
                        key={fax.messageId}
                        className="mt-2 flex items-start justify-between gap-2"
                      >
                        <div>
                          <Link
                            to={`/fax/sent/${fax.messageId}`}
                            className="hover:text-primary inline-flex items-center gap-1 text-xs text-gray-500 underline-offset-2 transition hover:underline"
                          >
                            <IconSend2 size={14} className="shrink-0" />
                            <span>
                              Faxed to {fax.recipient} · {formatNoteTimestamp(fax.sentAt)}
                            </span>
                            <IconExternalLink size={12} className="shrink-0" />
                          </Link>
                          {fax.note && (
                            <p className="mt-1 text-sm whitespace-pre-wrap text-gray-700">
                              {fax.note}
                            </p>
                          )}
                        </div>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Remove fax to ${fax.recipient}`}
                          onClick={() =>
                            unlinkFax.mutate({ itemId: item.id, messageId: fax.messageId })
                          }
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      </div>
                    ))}
                  </div>
                )}

                {item.completion && (
                  <div className="rounded-md border border-[#EEEEEE] p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCircleCheck size={14} className="shrink-0" />
                      <span>
                        Completed by {item.completion.by.display} ·{' '}
                        {formatNoteTimestamp(item.completion.at)}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm whitespace-pre-wrap text-gray-700">
                      {item.completion.note}
                    </p>
                  </div>
                )}
                {item.sourceLetterRequestId &&
                  (() => {
                    const patientCell = Object.values(item.values).find(
                      (v): v is CellRef =>
                        typeof v === 'object' && v !== null && v.reference.startsWith('Patient/'),
                    )
                    const patientId = patientCell?.reference.split('/')[1]
                    // The letter opens in review mode on the chart's Documents tab —
                    // a query param, not a path segment, so it can't go through
                    // buildPatientChartPath.
                    const href = patientId
                      ? `${buildPatientChartPath(patientId, 'documents')}?letterRequest=${item.sourceLetterRequestId}`
                      : undefined
                    const label = item.sourcePractitioner
                      ? `Prepared by ${item.sourcePractitioner.display} — open to review and sign`
                      : 'Open to review and sign'
                    return (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconFileText size={14} className="shrink-0" />
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary inline-flex items-center gap-1 underline-offset-2 transition hover:underline"
                          >
                            <span>{label}</span>
                            <IconExternalLink size={12} className="shrink-0" />
                          </a>
                        ) : (
                          <span>{label}</span>
                        )}
                      </div>
                    )
                  })()}

                <ChecklistSection
                  item={item}
                  onToggle={(stepId, done) =>
                    void toggleChecklistStep
                      .mutateAsync({ itemId: item.id, stepId, done })
                      .catch(() => undefined)
                  }
                  onSave={saveChecklist}
                />

                <section className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Updates</span>
                    {updateCount > 0 && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                        {updateCount}
                      </span>
                    )}
                  </div>

                  <NoteComposer
                    placeholder="Write an update…"
                    submitLabel="Update"
                    pending={addNote.isPending}
                    onSubmit={(text, attachments) => submitNote(text, undefined, attachments)}
                  />

                  {item.notes.length === 0 ? (
                    <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-[#E5E7EB] py-8">
                      <IconMessageCircle size={22} className="text-gray-300" />
                      <p className="text-sm font-medium text-gray-500">No updates yet</p>
                      <p className="text-xs text-gray-400">Post the first update on this task.</p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {item.notes.map((note) => (
                        <NoteCard
                          key={note.id}
                          note={note}
                          onReply={submitNote}
                          pending={addNote.isPending}
                        />
                      ))}
                    </div>
                  )}
                </section>
              </div>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>
    </>
  )
}
